import fp from 'lodash/fp';
import React, { ComponentType, RefObject, PropsWithChildren } from 'react';
import InfiniteLoader from 'react-window-infinite-loader';
import { VariableSizeList, VariableSizeGrid } from 'react-window';

import { multipleRef, AutoSizer } from 'Modules/ui';
import { useAction } from 'Modules/core/redux';

import { paginationActions } from '../../state/pagination/actions';
import { paginationStatuses } from '../../state/pagination/statuses';
import { usePagination } from '../../utils';
import { PaginationType } from '../../state/types';
import { IRequest } from '../../state/pagination/types';
import {
    CommonContainerProps,
    PaginationLoaderConnectedProps,
    PaginationLoaderProps,
} from './types';


const getNonPendingRequests = (queue: PaginationType['queue']): string[] =>
    fp.pipe(
        fp.pickBy(status => status !== paginationStatuses.PENDING),
        fp.keys,
    )(queue);

const forceUpdateContainer = (
    container: RefObject<VariableSizeList | VariableSizeGrid>['current'],
    startIndex: number,
) => {
    if ('resetAfterIndex' in container && fp.isFunction(container.resetAfterIndex)) {
        container.resetAfterIndex(startIndex, true);
        return;
    }

    // use internal property
    // @ts-ignore
    if (fp.isFunction(container._getItemStyleCache))
        // @ts-ignore
        container._getItemStyleCache(-1);

    container.forceUpdate();
};

class PaginationLoaderClass<
    TContainerProps extends CommonContainerProps,
> extends React.PureComponent<PaginationLoaderConnectedProps<TContainerProps>> {
    requests = {};

    infiniteLoaderRef = React.createRef<InfiniteLoader>();

    containerRef = React.createRef<VariableSizeList | VariableSizeGrid>();

    currentlyRendered = {
        visibleStartIndex: 0,
        visibleStopIndex: -1,
    };

    componentDidMount() {
        this.refreshPagination();
    }

    componentDidUpdate(prevProps: PaginationLoaderConnectedProps<TContainerProps>) {
        this.refreshPagination();
        this.resolvePendingRequests();
        this.refreshWhenDirty(prevProps);
    }

    resetInfiniteLoaderCache = () => {
        if (this.infiniteLoaderRef.current)
            this.infiniteLoaderRef.current.resetloadMoreItemsCache(true);
    };

    refreshPagination = () => {
        const { pagination, request } = this.props;

        if (pagination && !fp.isNumber(pagination.total)) {
            this.requests['0:0'] = this.resetInfiniteLoaderCache;
            if (pagination.queue['0:0'] !== paginationStatuses.PENDING)
                request({ start: 0, stop: 0 });
        }
    };

    resolvePendingRequests = () => {
        const { pagination } = this.props;

        if (!pagination)
            return;

        if (fp.isEmpty(this.requests))
            return;

        const completedRequests = fp.intersection(
            fp.keys(this.requests),
            getNonPendingRequests(pagination.queue),
        );

        completedRequests.forEach(key => {
            this.requests[key]();
            delete this.requests[key];
        });
    };

    refreshWhenDirty = ({
        pagination: oldPagination,
    }: PaginationLoaderConnectedProps<TContainerProps>) => {
        if (!oldPagination)
            return;

        const { refreshOnDirty, requestRefresh, pagination: { dirty = false } = {} } = this.props;
        const becameDirty = !oldPagination.dirty && dirty;

        if (!refreshOnDirty || !becameDirty)
            return;

        const start = this.currentlyRendered.visibleStartIndex || 0;
        let stop = this.currentlyRendered.visibleStopIndex + 1;
        stop = fp.isNumber(stop) ? stop : 0;

        this.requests[`${start}:${stop}`] = () => {
            this.resetInfiniteLoaderCache();
            if (this.containerRef.current)
                forceUpdateContainer(this.containerRef.current, start);
        };
        requestRefresh({ start, stop });
    };

    isItemLoaded = (index: number) => {
        const {
            pagination: { elements },
        } = this.props;
        return !!elements[index];
    };

    loadMoreItems = (start: number, stopIndex: number): Promise<void> => {
        const { request } = this.props;
        const stop = stopIndex + 1;
        return new Promise(resolve => {
            this.requests[`${start}:${stop}`] = resolve;
            request({ start, stop });
        });
    };

    render() {
        const {
            path,
            pagination,
            containerRef,
            placeholderRenderer,
            loadingRenderer,
            request,
            requestRefresh,
            ...props
        } = this.props;
        const itemCount = fp.getOr(0, 'total', pagination);

        const Placeholder = this.props.placeholderRenderer as ComponentType;
        const Loading = this.props.loadingRenderer as ComponentType;
        const Container = this.props.container as ComponentType<TContainerProps>;
        const containerProps = props as unknown as TContainerProps;

        const isLoading = pagination && !fp.isNumber(pagination.total);
        const noItems = pagination && !isLoading && itemCount === 0;

        return (
            <AutoSizer>
                {size => (
                    <InfiniteLoader
                        ref={this.infiniteLoaderRef}
                        key={path}
                        isItemLoaded={this.isItemLoaded}
                        loadMoreItems={this.loadMoreItems}
                        itemCount={itemCount}
                        minimumBatchSize={20}
                        threshold={10}
                    >
                        {({ onItemsRendered, ref }) => (
                            <>
                                <Container
                                    ref={multipleRef(ref, containerRef, this.containerRef)}
                                    {...containerProps}
                                    itemCount={itemCount}
                                    onItemsRendered={indices => {
                                        this.currentlyRendered = indices;
                                        return onItemsRendered(indices);
                                    }}
                                    {...size}
                                />
                                {isLoading && Loading && (
                                    <div className='acover'>
                                        <Loading />
                                    </div>
                                )}
                                {noItems && Placeholder && <Placeholder />}
                            </>
                        )}
                    </InfiniteLoader>
                )}
            </AutoSizer>
        );
    }
}


export function PaginationLoader<TContainerProps extends CommonContainerProps>(
    props: PropsWithChildren<PaginationLoaderProps<TContainerProps>>
    & { userSetVScrollBarVisible?: boolean },
) {
    const { path } = props;
    const pagination = usePagination(path);
    const request = useAction((data: IRequest) => paginationActions.request(path, data), [path]);
    const requestRefresh = useAction(
        (data: IRequest) => paginationActions.requestRefresh(path, data),
        [path],
    );

    return (
        <PaginationLoaderClass
            pagination={pagination}
            request={request}
            requestRefresh={requestRefresh}
            {...props}
        />
    );
}
